import axios from 'axios';

const STORYBOOK_API_BASE = '/storybook-api';

export type TaskStatus = 'queued' | 'running' | 'succeeded' | 'failed';
export type BookLayout =
  | 'full_image_16_9'
  | 'text_image_h_3_4'
  | 'image_text_v_3_4'
  | 'image_text_v_1_1'
  | 'cross_page_3_4'
  | 'cross_page_1_1';
export type ImageProvider = 'tencent' | 'kie' | 'huoshan';
export type ImagePhase = 'character_previews' | 'cover' | 'pages';

export interface TaskSubmitResponse {
  task_id: string;
  status: TaskStatus;
  poll_url: string;
}

export interface TaskSnapshot<T = any> {
  task_id: string;
  task_type: string;
  status: TaskStatus;
  progress: number;
  stage: string;
  message: string;
  result: T | null;
  error?: string | null;
  created_at: number;
  updated_at: number;
  started_at?: number | null;
  finished_at?: number | null;
  metadata: Record<string, any>;
}

export interface StoryAppearance {
  age_look?: string;
  hair?: string;
  eyes?: string;
  skin?: string;
  build?: string;
  outfit?: string;
  distinctive_feature?: string;
}

export interface StoryCharacterForm {
  id: string;
  name: string;
  description?: string;
  appearance?: StoryAppearance;
  consistency_prompt?: string;
  visual_keywords?: string[];
  reference_image_url?: string;
  generated_reference_image_url?: string;
  input_reference_image_url?: string;
}

export interface StoryCharacter {
  id: string;
  name: string;
  role?: string;
  appearance?: StoryAppearance;
  personality?: string;
  consistency_prompt?: string;
  visual_keywords?: string[];
  reference_image_url?: string;
  generated_reference_image_url?: string;
  input_reference_image_url?: string;
  forms?: StoryCharacterForm[];
  default_form_id?: string | null;
}

export interface StoryPage {
  page: number;
  narration: string;
  emotion?: string;
  tts_style?: string;
  scene_description?: string;
  scene_semantic?: Record<string, any>;
  image_url?: string;
}

export interface StoryCoverCharacter {
  id: string;
  form_id?: string | null;
  action?: string;
  position?: string;
  expression?: string;
}

export interface StoryCoverSpec {
  description?: string;
  character_ids?: string[];
  named_characters?: StoryCoverCharacter[];
}

export interface StoryBook {
  title: string;
  language: string;
  language_code?: string;
  theme: string;
  target_age?: string;
  story_type?: string;
  art_style: string;
  art_style_keywords?: string;
  style_ref_image_url?: string;
  moral?: string;
  cover_description?: string;
  cover?: StoryCoverSpec;
  characters: StoryCharacter[];
  pages: StoryPage[];
}

export interface StoryCreatePayload {
  theme: string;
  language: string;
  language_code?: string;
  art_style: string;
  story_idea?: string;
  target_age?: string;
  perspective?: string;
  page_count?: { min: number; max: number };
  character_setting?: string;
  mood?: string;
  moral_goal?: string;
  extra_requirement?: string;
  layout?: BookLayout;
  aspect_ratio?: string;
  page_aspect_ratio?: string;
  character_aspect_ratio?: string;
  cover_aspect_ratio?: string;
}

export interface StoryCreateResult {
  story: StoryBook;
  [key: string]: any;
}

export interface StoryOutlineCharacter extends StoryCharacter {
  consistency_prompt?: string;
  visual_keywords?: string[];
}

export interface StoryOutlinePage {
  page: number;
  narration: string;
  character_ids?: string[];
}

export interface StoryOutline {
  title: string;
  language: string;
  language_code?: string;
  theme: string;
  target_age?: string;
  story_type?: string;
  moral?: string;
  characters: StoryOutlineCharacter[];
  pages: StoryOutlinePage[];
}

export interface StoryOutlineResult {
  outline: StoryOutline;
  layout?: BookLayout;
  aspect_ratio?: string;
  generation_mode?: string;
  [key: string]: any;
}

export type StoryOutlineCreatePayload = Omit<
  StoryCreatePayload,
  'layout' | 'aspect_ratio' | 'page_aspect_ratio' | 'character_aspect_ratio' | 'cover_aspect_ratio'
>;

export interface StoryOutlineRepairPayload {
  request: StoryOutlineCreatePayload;
  outline: StoryOutline;
  edit_instruction?: string;
}

export interface StoryOutlineRepairResult extends StoryOutlineResult {
  changes?: {
    added_characters?: StoryOutlineCharacter[];
    updated_characters?: StoryOutlineCharacter[];
    removed_character_ids?: string[];
    warnings?: string[];
    [key: string]: any;
  };
}

/** 对齐 /images payload；complete 时由服务端生成 story，故不含 story */
export type StoryCompleteImagesPayload = Omit<ImageGeneratePayload, 'story'>;

export interface StoryCompletePayload {
  request: StoryCreatePayload;
  outline: StoryOutline;
  /** 与 /images 同结构的生图配置（不含 story） */
  images?: StoryCompleteImagesPayload;
}

export interface StoryCompleteResult extends StoryOutlineResult {
  story: StoryBook;
}

export type StoryStreamEventName =
  | 'delta'
  | 'field'
  | 'page'
  | 'narration_delta'
  | 'result'
  | 'error'
  | 'done';

export interface StoryStreamFieldEvent {
  field?: keyof StoryBook | string;
  name?: keyof StoryBook | string;
  key?: keyof StoryBook | string;
  value?: any;
}

export interface StoryNarrationDeltaEvent {
  page?: number;
  page_index?: number;
  index?: number;
  delta?: string;
  narration_delta?: string;
  text?: string;
  content?: string;
  value?: string;
}

export type StoryStreamTaskStatus =
  | 'queued'
  | 'ready'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'expired';

export interface StoryStreamTaskSnapshot {
  task_id: string;
  status: StoryStreamTaskStatus;
  queue_position?: number | null;
  poll_url?: string;
  stream_url?: string;
  message?: string;
  error?: string | null;
  result?: StoryCreateResult | null;
  [key: string]: any;
}

export interface StoryStreamHandlers {
  onDelta?: (chunk: string) => void;
  onField?: (field: StoryStreamFieldEvent | string) => void;
  onPage?: (page: StoryPage) => void;
  onNarrationDelta?: (delta: StoryNarrationDeltaEvent | string) => void;
  onTaskUpdate?: (task: StoryStreamTaskSnapshot) => void;
  onResult?: (result: StoryCreateResult) => void;
  onError?: (message: string) => void;
  signal?: AbortSignal;
}

export interface PageScriptEdit {
  page: number;
  narration?: string;
  scene_description?: string;
  edit_instruction?: string;
}

export interface StoryboardRegeneratePayload {
  story: StoryBook;
  edits: PageScriptEdit[];
  layout?: BookLayout;
  aspect_ratio?: string;
}

export interface StoryboardRegenerateResult {
  story: StoryBook;
  updated_pages?: number[];
  [key: string]: any;
}

export interface ImageGeneratePayload {
  story: StoryBook;
  layout?: BookLayout;
  aspect_ratio?: string;
  image_provider?: ImageProvider;
  phases?: ImagePhase[];
  page_aspect_ratio?: string;
  character_aspect_ratio?: string;
  cover_aspect_ratio?: string;
  retry?: {
    max_attempts?: number;
    retry_wait_seconds?: number;
    img2img_max_attempts?: number;
    img2img_retry_wait_seconds?: number;
  };
}

export interface ImageGenerationStatsResponse {
  character_failures: Array<Record<string, string>>;
  page_failures: Array<Record<string, string>>;
  cover_failures: Array<Record<string, string>>;
  img2img_fallbacks: Array<Record<string, string>>;
  img2img_retry_failures: Array<Record<string, string>>;
  image_timings: Array<Record<string, string | number>>;
}

export interface ImageGenerateResult {
  story: StoryBook;
  cover?: {
    image_url?: string;
    prompt?: string;
    description?: string;
    character_ids?: string[];
    reference_image_urls?: string[];
    reference_mappings?: string[];
  };
  pages?: Array<{ page: number; image_url?: string; prompt?: string }>;
  stats?: ImageGenerationStatsResponse;
  [key: string]: any;
}

export interface CharacterDesignPayload {
  name: string;
  description: string;
  art_style: string;
  image_provider?: ImageProvider;
  image_model?: string;
  aspect_ratio?: string;
  callback_url?: string;
  retry?: {
    max_attempts?: number;
    retry_wait_seconds?: number;
    img2img_max_attempts?: number;
    img2img_retry_wait_seconds?: number;
  };
}

export interface CharacterDesignResult {
  image_url: string;
  prompt?: string;
  source_image_url?: string;
  image_provider?: ImageProvider | string;
  art_style?: string;
  aspect_ratio?: string;
  [key: string]: any;
}

export interface CharacterStylizePayload {
  image_url: string;
  name?: string;
  description?: string;
  art_style: string;
  image_provider?: ImageProvider;
  aspect_ratio?: string;
}

export interface CharacterStylizeResult {
  image_url: string;
  prompt?: string;
  source_image_url?: string;
  image_provider?: ImageProvider | string;
  art_style?: string;
  aspect_ratio?: string;
  [key: string]: any;
}

export async function createStory(payload: StoryCreatePayload): Promise<TaskSubmitResponse> {
  const res = await axios.post(`${STORYBOOK_API_BASE}/api/v1/stories`, payload);
  return res.data;
}

export async function createStoryOutline(
  payload: StoryOutlineCreatePayload
): Promise<TaskSubmitResponse> {
  const res = await axios.post(`${STORYBOOK_API_BASE}/api/v1/stories/outline`, payload);
  return res.data;
}

export async function repairStoryOutline(
  payload: StoryOutlineRepairPayload
): Promise<TaskSubmitResponse> {
  const res = await axios.post(`${STORYBOOK_API_BASE}/api/v1/stories/outline/repair`, payload);
  return res.data;
}

export async function completeStoryFromOutline(
  payload: StoryCompletePayload
): Promise<TaskSubmitResponse> {
  const res = await axios.post(`${STORYBOOK_API_BASE}/api/v1/stories/complete`, payload);
  return res.data;
}

const parseStoryStreamData = (data: string) => {
  if (!data || data === '[DONE]') return null;

  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
};

const resolveStorybookUrl = (url?: string) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${STORYBOOK_API_BASE}${url.startsWith('/') ? url : `/${url}`}`;
};

const waitForStoryStreamTaskReady = async (
  taskId: string,
  handlers: StoryStreamHandlers,
  intervalMs = 1500,
  timeoutMs = 300000
): Promise<StoryStreamTaskSnapshot> => {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    const snapshot = await getStoryStreamTask(taskId);
    handlers.onTaskUpdate?.(snapshot);

    if (snapshot.status === 'ready') return snapshot;
    if (snapshot.status === 'failed')
      throw new Error(snapshot.error || snapshot.message || '故事生成任务失败');
    if (snapshot.status === 'expired') throw new Error('故事生成任务已过期，请重新提交');
    if (snapshot.status === 'succeeded') return snapshot;

    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  throw new Error('故事生成任务排队超时');
};

async function readStoryStreamEvents(
  streamUrl: string,
  handlers: StoryStreamHandlers = {}
): Promise<StoryCreateResult | null> {
  const response = await fetch(streamUrl, {
    method: 'GET',
    headers: {
      Accept: 'text/event-stream'
    },
    signal: handlers.signal
  });

  if (!response.ok) {
    throw new Error(`故事生成失败：${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error('当前浏览器不支持流式响应');

  const decoder = new TextDecoder();
  let buffer = '';
  let eventName: StoryStreamEventName = 'delta';
  let dataLines: string[] = [];
  let finalResult: StoryCreateResult | null = null;

  const dispatchEvent = () => {
    if (!dataLines.length) return;

    const data = parseStoryStreamData(dataLines.join('\n'));
    if (data === null) return;

    switch (eventName) {
      case 'delta':
        handlers.onDelta?.(typeof data === 'string' ? data : (data.delta ?? data.text ?? ''));
        break;
      case 'field':
        handlers.onField?.(data);
        break;
      case 'page':
        handlers.onPage?.(data as StoryPage);
        break;
      case 'narration_delta':
        handlers.onNarrationDelta?.(data);
        break;
      case 'result':
        finalResult = data as StoryCreateResult;
        handlers.onResult?.(finalResult);
        break;
      case 'error': {
        const message =
          typeof data === 'string' ? data : (data.message ?? data.error ?? '故事生成失败');
        handlers.onError?.(message);
        throw new Error(message);
      }
      case 'done':
        break;
      default:
        break;
    }
  };

  const processLine = (line: string) => {
    if (!line) {
      dispatchEvent();
      eventName = 'delta';
      dataLines = [];
      return;
    }

    if (line.startsWith(':')) return;

    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim() as StoryStreamEventName;
      return;
    }

    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trimStart());
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split(/\r?\n/);
    buffer = lines.pop() ?? '';
    lines.forEach(processLine);
  }

  buffer += decoder.decode();
  if (buffer) processLine(buffer);
  dispatchEvent();

  return finalResult;
}

export async function submitStoryStreamTask(
  payload: StoryCreatePayload
): Promise<StoryStreamTaskSnapshot> {
  const res = await axios.post(`${STORYBOOK_API_BASE}/api/v1/stories/stream-tasks`, payload);
  return res.data;
}

export async function getStoryStreamTask(taskId: string): Promise<StoryStreamTaskSnapshot> {
  const res = await axios.get(`${STORYBOOK_API_BASE}/api/v1/stories/stream-tasks/${taskId}`);
  return res.data;
}

export async function createStoryStream(
  payload: StoryCreatePayload,
  handlers: StoryStreamHandlers = {}
): Promise<StoryCreateResult | null> {
  const submitted = await submitStoryStreamTask(payload);
  handlers.onTaskUpdate?.(submitted);

  const readyTask =
    submitted.status === 'ready' || submitted.status === 'succeeded'
      ? submitted
      : await waitForStoryStreamTaskReady(submitted.task_id, handlers);

  if (readyTask.status === 'succeeded') return readyTask.result ?? null;

  const streamUrl = resolveStorybookUrl(
    readyTask.stream_url ?? `/api/v1/stories/stream-tasks/${readyTask.task_id}/events`
  );

  return readStoryStreamEvents(streamUrl, handlers);
}

export async function regenerateStoryboard(
  payload: StoryboardRegeneratePayload
): Promise<TaskSubmitResponse> {
  const res = await axios.post(`${STORYBOOK_API_BASE}/api/v1/storyboards/regenerate`, payload);
  return res.data;
}

export async function generateImages(payload: ImageGeneratePayload): Promise<TaskSubmitResponse> {
  const res = await axios.post(`${STORYBOOK_API_BASE}/api/v1/images`, payload);
  return res.data;
}

export async function designCharacter(
  payload: CharacterDesignPayload
): Promise<TaskSubmitResponse> {
  const res = await axios.post(`${STORYBOOK_API_BASE}/api/v1/characters/design`, payload);
  return res.data;
}

export async function stylizeCharacter(
  payload: CharacterStylizePayload
): Promise<TaskSubmitResponse> {
  const res = await axios.post(`${STORYBOOK_API_BASE}/api/v1/characters/stylize`, payload);
  return res.data;
}

export async function getTask<T = any>(taskId: string): Promise<TaskSnapshot<T>> {
  const res = await axios.get(`${STORYBOOK_API_BASE}/api/v1/tasks/${taskId}`);
  return res.data;
}

export async function pollTask<T = any>(
  taskId: string,
  options: {
    intervalMs?: number;
    timeoutMs?: number;
    onUpdate?: (task: TaskSnapshot<T>) => void;
  } = {}
): Promise<TaskSnapshot<T>> {
  const intervalMs = options.intervalMs ?? 3000;
  const timeoutMs = options.timeoutMs ?? 900000;
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    try {
      const task = await getTask<T>(taskId);
      options.onUpdate?.(task);

      if (task.status === 'succeeded' || task.status === 'failed') return task;
    } catch (error) {
      // 偶发网关超时等瞬时错误（如 504）忽略，继续轮询
      console.warn('[task-progress] 查询失败，将重试:', error);
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  throw new Error('任务轮询超时');
}
